import {
  invTotals,
  invoiceParty,
  invoicePartyError,
} from "../../domain/invoicing.js";
import { S, ui } from "../../app/store.js";
import { P, peso } from "../../domain/format.js";
import { fmtD, today } from "../../domain/dates.js";
import { siteCode } from "../sites/model.js";
import { nextJV } from "../../domain/ledger.js";
import { log } from "../../services/audit.js";

const client = (id) => S.clients.find((c) => c.id === id);

function issueInvoice(d) {
  const co = S.company,
    cl = client(d.client),
    p = S.projects.find((x) => x.code === d.project);
  const sellerError = invoicePartyError(co, "Seller");
  if (sellerError)
    return `${sellerError} Update Accounting › Overview › Registration before issuing an invoice.`;
  if (!cl)
    return "Choose the client, or add a new one with its TIN and business address.";
  if (p && p.cid !== cl.id)
    return "Choose the client assigned to this project.";
  const buyerError = invoicePartyError(cl, "Buyer");
  if (buyerError)
    return `${buyerError} Update the client details before invoicing.`;
  const net = P(+String(d.amount).replace(/,/g, "") || 0);
  if (net <= 0) return "Enter the amount before VAT.";
  if (!d.desc.trim()) return "Describe the services billed.";
  if (!d.date || d.date <= S.lockedThrough)
    return `Books are locked through ${fmtD(S.lockedThrough)}. Date the invoice after that.`;
  const ret = Math.max(0, +d.ret || 0),
    t = invTotals(net, ret),
    unb = p ? Math.max(0, p.recog - p.billed) : 0,
    fromUnb = p ? Math.min(net, unb) : 0;
  const inv = {
    no: "SI-" + String(++S.seq.inv).padStart(6, "0"),
    date: d.date,
    client: cl.id,
    project: p?.code || "",
    siteId: p?.siteId || "",
    siteCode: p ? siteCode(p) : "",
    siteName: p?.site || "",
    desc: d.desc.trim(),
    ...t,
    retPct: ret,
    atc: d.svc === "WC120" ? "WC120" : "WC011",
    status: "UNPAID",
    by: ui.user,
    seller: { ...co, ...invoiceParty(co) },
    buyer: invoiceParty(cl),
  };
  const lines = [{ a: "1110", d: t.due, c: 0 }];
  if (t.ret) lines.push({ a: "1120", d: t.ret, c: 0 });
  if (d.svc === "WC120") lines.push({ a: "4020", d: 0, c: net });
  else if (p) {
    if (fromUnb) lines.push({ a: "1130", d: 0, c: fromUnb });
    if (net - fromUnb) lines.push({ a: "2120", d: 0, c: net - fromUnb });
    p.billed += net;
  } else lines.push({ a: "4010", d: 0, c: net });
  lines.push({ a: "2240", d: 0, c: t.vat });
  const jv = {
    id: nextJV(),
    project: p?.code || "",
    date: d.date,
    memo: `Sales invoice ${inv.no}, ${cl.name}`,
    lines,
    status: "POSTED",
    by: ui.user,
    ap: ui.user,
    src: inv.no,
  };
  S.je.push(jv);
  inv.je = jv.id;
  S.invoices.push(inv);
  log("Issued invoice", `${inv.no} ${cl.name} ${peso(t.gross)}`);
  return inv;
}

function payInvoice(no) {
  const i = S.invoices.find((x) => x.no === no);
  if (!i || i.status !== "UNPAID") return;
  const cwt = Math.round(
      i.net * (1 - i.retPct / 100) * (i.atc === "WC120" ? 0.02 : 0.15),
    ),
    jv = {
      id: nextJV(),
      date: today(),
      memo: `Payment on ${i.no}; ${i.atc === "WC120" ? "2% CWT (WC120)" : "15% CWT (WC011)"}`,
      src: i.no,
      status: "POSTED",
      by: ui.user,
      ap: ui.user,
      lines: [
        { a: "1020", d: i.due - cwt, c: 0 },
        { a: "1420", d: cwt, c: 0 },
        { a: "1110", d: 0, c: i.due },
      ],
    };
  S.je.push(jv);
  i.status = "PAID";
  i.paidOn = today();
  i.cwt = cwt;
  i.f2307 = "PENDING";
  log("Recorded payment", `${i.no} net of ${peso(cwt)} CWT`);
}

function invDefaults(projectCode) {
  const p = S.projects.find((x) => x.code === projectCode);
  return p
    ? {
        svc: "WC011",
        project: p.code,
        amount: (Math.max(0, p.recog - p.billed) / 100).toFixed(2),
        desc: `Progress billing, ${p.name} (${p.pct}% complete)`,
      }
    : { svc: "WC011", project: "", amount: "", desc: "" };
}

export { invTotals, client, issueInvoice, payInvoice, invDefaults };
